import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import MasterLayout from '../../components/layouts/masterlayout';
import ErrorMessage from '../../components/common/errormessage';
import SuccessMessage from '../../components/common/successmessage';
import GeneralTextBox from '../common/textboxes/textbox_general';
import GeneralDropdown from '../common/dropdowns/dropdown_general';
import GeneralButton from '../common/button_general';
import Spinner from '../common/spinner';
import { getHeaders, handleErrorResponse, handleResponse } from '../../lib/requests';
import ClientModal from '../common/modals/client_modal';
import RouteModal from '../common/modals/route_modal';
import { fetchClientsForRoute } from '../../services/delivery_services';
import { UserContext } from '../../lib/usercontext';

function DeliveryForm({ delivery = {} }) {
    const { deliveryId } = useParams();
    const [isLoading, setIsLoading] = useState(false);
    const [showError, setShowError] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
    const [isRouteModalOpen, setIsRouteModalOpen] = useState(false);
    const navigate = useNavigate();

    const { user } = useContext(UserContext);
    const isAdmin = (user?.userGroups || []).some(group => group.toLowerCase() === 'admin');

    const [formData, setFormData] = useState({
        route_id: null,
        route_name: delivery.route_name || null,
        client_id: isAdmin ? delivery.client_id || null : null,
        order_group_id: delivery.order_group_id || null,
        scheduled_delivery_date: delivery.scheduled_delivery_date || null,
        delivery_date: delivery.delivery_date || delivery.scheduled_delivery_date || null,
        status: delivery.status || null,
    });

    const [selectedClient, setSelectedClient] = useState({ client_id: null, name: '' });
    const [selectedRoute, setSelectedRoute] = useState({ route_id: null, name: '', delivery_day_of_week: null });
    const [clientsForRoute, setClientsForRoute] = useState([]);

    useEffect(() => {
        if (!deliveryId) {
            setFormData(prevState => ({ ...prevState, status: 'scheduled' }));
            return;
        }
        const fetchDeliveryData = async () => {
            try {
                setIsLoading(true);
                const apiToken = localStorage.getItem('accessToken');
                const apiUrl = process.env.REACT_APP_API_URL;
                const response = await axios.get(`${apiUrl}/deliveries/${deliveryId}`, { headers: getHeaders(apiToken) });
                
                const formattedDeliveryDate = response.data.data.delivery_date
                    ? new Date(response.data.data.delivery_date).toISOString().split('T')[0]
                    : new Date(response.data.data.scheduled_delivery_date).toISOString().split('T')[0];
                const formattedData = {
                    ...response.data.data,
                    scheduled_delivery_date: formattedDeliveryDate,
                    delivery_date: formattedDeliveryDate,
                };
                setFormData(formattedData);

                setSelectedClient({ client_id: response.data.data.client_id, name: response.data.data.client_name });
                setSelectedRoute({ route_id: response.data.data.route_id, name: response.data.data.route_name, delivery_day_of_week: response.data.data.delivery_day_of_week });

                setShowError(false);
            } catch (error) {
                handleErrorResponse(error, navigate, setShowError, setErrorMessage);
            } finally {
                setIsLoading(false);
            }
        };
        fetchDeliveryData();
    }, [deliveryId]);

    const handleClientSelect = (client) => {
        if (!isAdmin) return;
        setFormData(prevState => ({ ...prevState, client_id: client.client_id }));
        setSelectedClient(client);
    };

    const handleRouteSelect = async (route) => {
        if (route.route_id === selectedRoute.route_id) return;

        setFormData(prevState => ({
            ...prevState,
            route_id: route.route_id,
            delivery_date: prevState.delivery_date ||
                           getNextDateForDayOfWeek(route.delivery_day_of_week),
            scheduled_delivery_date: prevState.delivery_date ||
                            getNextDateForDayOfWeek(route.delivery_day_of_week)
        }));

        setSelectedRoute(route);

        const clients = await fetchClientsForRoute(route.route_id);
        setClientsForRoute(clients);

        if (!isAdmin && clients.length) {
            const firstClient = clients[0];
            setFormData(prevState => ({ ...prevState, client_id: firstClient.client_id }));
            setSelectedClient(firstClient);
        }
    };

    const getNextDateForDayOfWeek = (dayOfWeek) => {
        const today = new Date();
        const resultDate = new Date(today);
        resultDate.setDate(today.getDate() + (dayOfWeek + 7 - today.getDay()) % 7);
        return resultDate.toISOString().split('T')[0];
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevState => ({ ...prevState, [name]: value }));
    };

    const resetForm = () => {
        setSuccessMessage('');
        setErrorMessage('');
        setShowError(false);
        setShowSuccess(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isCustomerModalOpen || isRouteModalOpen) return;

        resetForm();
        if (!formData.delivery_date || !formData.route_id || (isAdmin && !formData.client_id)) {
            setShowError(true);
            setErrorMessage('Delivery Date and Route are required' + (isAdmin ? ', Provider is required' : ''));
            return;
        }

        const apiToken = localStorage.getItem('accessToken');
        const apiUrl = process.env.REACT_APP_API_URL;
        if (!apiToken || !apiUrl) {
            setShowError(true);
            setErrorMessage('Configuration error');
            return;
        }

        const headers = getHeaders(apiToken);
        const url = deliveryId ? `${apiUrl}/deliveries/${deliveryId}` : `${apiUrl}/deliveries`;
        const method = deliveryId ? 'put' : 'post';

        let submissionData = { ...formData, scheduled_delivery_date: formData.delivery_date };
        if (!isAdmin) delete submissionData.client_id;

        const transformedFormData = Object.fromEntries(
            Object.entries(submissionData).map(([key, value]) => [key, value === '' ? null : value])
        );

        try {
            const response = await axios({ method, url, data: transformedFormData, headers });
            handleResponse(response,
                "delivery",
                setShowSuccess,
                setSuccessMessage,
                setShowError,
                setErrorMessage,
                navigate,
                '/deliveries/:id')
        } catch (error) {
            handleErrorResponse(error, navigate, setShowError, setErrorMessage);
        }
    };

    const handleBack = () => navigate('/deliveries');

    const pageTitle = deliveryId ? 'Edit Delivery' : 'Create Delivery';

    return (
        <MasterLayout pageTitle={pageTitle}>
            {showError && <ErrorMessage message={errorMessage} />}
            {showSuccess && <SuccessMessage message={successMessage} setShowSuccess={setShowSuccess} />}
            {isLoading ? <Spinner /> : (
                <form onSubmit={handleSubmit} className="p-4">
                    <div className={`grid gap-6 mb-6 ${deliveryId ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>                        
                        <GeneralTextBox
                            label="Delivery Date" id="delivery_date" name="delivery_date" type="date"
                            value={formData.delivery_date} onChange={handleChange}
                            placeholder="YYYY-MM-DD" required={true}
                            disabled={formData.status === 'delivered'}
                        />
                        <RouteModal
                            isOpen={isRouteModalOpen}
                            setIsOpen={setIsRouteModalOpen}
                            selectRoute={handleRouteSelect}
                            value={selectedRoute}
                            isRequired={true}
                        />
                        {isAdmin && (
                        <ClientModal
                            isOpen={isCustomerModalOpen}
                            setIsOpen={setIsCustomerModalOpen}
                            selectClient={handleClientSelect}
                            value={selectedClient}
                            clients={clientsForRoute}
                            isRequired={true}
                        />
                        )}
                        {deliveryId && (<GeneralDropdown
                            label="Status" id="status" name="status"
                            value={formData.status} onChange={handleChange}
                            options={[
                                { value: "scheduled", name: "Scheduled" },
                                { value: "packaged", name: "Packaged" },
                                { value: "delivered", name: "Delivered" },
                                { value: "cancelled", name: "Cancelled" }
                            ]}
                            disabled={formData.status === 'delivered' || formData.status === 'cancelled'}
                            placeholder="Select Status" required={true}
                        />)}
                    </div>

                    <div className="flex justify-center mt-6">
                        <GeneralButton type="submit">
                            {deliveryId ? 'Update Delivery' : 'Create Delivery'}
                        </GeneralButton>
                        <GeneralButton type="button" onClick={handleBack} buttonType="secondary">
                            Back to Delivery List
                        </GeneralButton>
                    </div>
                    {deliveryId && (
                        <div className="flex justify-center mt-6">
                            <GeneralButton type="button" onClick={() => navigate(`/orders/create?delivery_id=${deliveryId}`)} buttonType="secondary" disabled={formData.status !== 'scheduled'}>
                                Add Order to Delivery
                            </GeneralButton>
                            <GeneralButton type="button" onClick={() => navigate(`/orders?delivery_id=${deliveryId}`)} buttonType="secondary">
                                View Orders
                            </GeneralButton>
                            <GeneralButton type="button" onClick={() => navigate(`/orders?delivery_id=${deliveryId}&addAllOrders=true`)} buttonType="secondary">
                                Add All Client Orders
                            </GeneralButton>
                        </div>
                    )}
                </form>
            )}
        </MasterLayout>
    );
}

export default DeliveryForm;
